import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProCoverVideo from '../components/pro/ProCoverVideo';
import BrandedQR from '../components/BrandedQR';
import { PRO_MAGENTA, MUTED, GREEN_ON_WHITE } from '../components/pro/proUi';
import { useAuth } from '../context/AuthContext';
import { proService } from '../services';
import { craftLabel } from '../utils/proCrafts';
import { brandPath } from '../utils/brand';

/**
 * ProLandingPage — /pro, the page that pitches a Pro page to a working professional and
 * sends them into Pro onboarding. Direction 1C "Stanchion" (design handoff, Sept 2026):
 * the Outside theme — black canvas, white mono type, magenta as punctuation — and one
 * warm surface, a mock of a real published Pro page, so the visitor sees the thing
 * they're getting before they read a word about it.
 *
 * Public: it's most often reached by a cold shared link. The only conversion action is
 * "Create your Pro page" (three instances, one destination): signed-out → register, then
 * on to /pro/setup; signed-in → /pro/setup (or "Manage" if they already have a page).
 * "See a live one" is a random real, fully built Pro page (GET /pro/showcase), falling
 * back to the directory when nobody qualifies yet.
 *
 * The 93-item craft wall the old page led with is gone — the hero copy names the
 * examples instead. No craft fetch, no state beyond auth + the showcase pick.
 */

/** The design's one structural gray: every band divider on the black ground. */
const RULE = '#262626';
/** Micro-labels, captions and secondary text on black. */
const DIM = '#8A8A8A';
const BODY = 'rgba(255,255,255,0.72)';

/** The white scrim over the mock's mosaic — lifted verbatim from ProHeader.jsx. */
const FADE = 'linear-gradient(90deg, rgba(255,255,255,.9) 1%, rgba(255,255,255,.8) 36%, rgba(255,255,255,.7) 46%, rgba(255,255,255,.55) 55%, rgba(255,255,255,0) 63%)';
/** Below md the mosaic is a band above the text, so the scrim runs bottom-up instead. */
const FADE_UP = 'linear-gradient(180deg, rgba(255,255,255,0) 40%, rgba(255,255,255,.55) 70%, rgba(255,255,255,.9) 100%)';

const ASSETS = '/pro-landing';

/** The sample pro. Fictional — invented for the mock; no real customer appears here. */
const MOCK = {
  name: 'Hollow Moon Sound',
  crafts: ['dj', 'sound-engineer', 'lighting-design'],
  vibe: 'Loud enough for the back row, quiet enough to talk at the bar.',
  city: 'Penticton, BC',
  site: 'hollowmoonsound.ca',
  owner: 'Ren Okada',
  handle: 'renokada',
  url: brandPath('pro/hollow-moon-sound'),
};

const STEPS = [
  { n: '01', t: 'Build your page', d: 'Name, crafts, bio, service menu, work gallery, team, links, availability — one form.' },
  { n: '02', t: 'Publish', d: `You get a public page at ${brandPath('pro/your-name')}. Share it anywhere.` },
  { n: '03', t: 'Get booked', d: 'Anyone can send you an offer — even people without an account. You negotiate + accept from your desk.' },
];

const CLAIMS = [
  'A service menu with your prices, so an offer arrives already close.',
  'The nights you work — hosts see your availability before they ask.',
  'A link and a QR code you can put anywhere: bio, van, business card.',
  'Planners propose you to their hosts, and the booking desk handles the rest.',
];

const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
// AM: Fri + Sat. PM: Thu + Fri + Sat.
const AM_ON = [5, 6];
const PM_ON = [4, 5, 6];

const FOCUS = 'focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black';
const BAND_X = 'px-5 md:px-14';
const INNER = 'mx-auto w-full max-w-7xl';

export default function ProLandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Signed in: which Pro pages do they already manage? undefined = not known yet, [] = none.
  const [pages, setPages] = useState(undefined);
  // The real page "See a live one" opens. undefined = loading, null = nobody qualifies.
  const [showcase, setShowcase] = useState(undefined);

  useEffect(() => {
    let done = false;
    proService.getShowcase()
      .then((r) => { if (!done) setShowcase(r?.success ? (r.data || null) : null); })
      .catch(() => { if (!done) setShowcase(null); });
    return () => { done = true; };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) { setPages([]); return undefined; }
    let done = false;
    proService.listMine()
      .then((r) => { if (!done) setPages(r?.success ? (r.data || []) : []); })
      .catch(() => { if (!done) setPages([]); });
    return () => { done = true; };
  }, [isAuthenticated]);

  const hasPage = (pages || []).length > 0;
  const ctaLabel = hasPage ? 'Manage your Pro page' : 'Create your Pro page';
  // Already running a page? A second one starts blank at /pro/setup?new=1.
  const newPageLink = hasPage && (
    <Link
      to="/pro/setup?new=1"
      className={`border-b-2 border-white pb-[3px] text-[12px] font-bold uppercase leading-none tracking-[0.08em] text-white transition-colors duration-200 hover:border-[#FF00FF] hover:text-[#FF00FF] ${FOCUS}`}
      data-testid="new-pro-page"
    >
      + New Pro page
    </Link>
  );
  const goCreate = () => {
    if (isAuthenticated) navigate('/pro/setup');
    else navigate(`/register?redirect=${encodeURIComponent('/pro/setup')}`);
  };
  const liveHref = showcase?.slug ? `/pro/${showcase.slug}` : '/pros';
  const shareUrl = showcase?.slug ? brandPath(`pro/${showcase.slug}`) : brandPath('pros');

  return (
    <div className="min-h-screen bg-black font-mono text-white" data-testid="pro-landing">
      {/* ── Band 1 — Top bar ─────────────────────────────────────────────── */}
      <header className={`flex h-[72px] items-center gap-3 md:gap-5 ${BAND_X}`} style={{ borderBottom: `2px solid ${RULE}` }}>
        <button
          type="button"
          onClick={() => navigate(isAuthenticated ? '/featured' : '/')}
          className={`flex items-center gap-3 md:gap-5 ${FOCUS}`}
          aria-label="Portal home"
        >
          <img src={`${ASSETS}/ball-64.png`} alt="" width={32} height={32} className="block h-8 w-8" />
          <span className="text-[16px] font-bold uppercase leading-none tracking-[0.1em]">Portal</span>
        </button>
        <span className="px-2 py-[3px] text-[10px] font-bold leading-none tracking-[0.14em]" style={{ border: `2px solid ${PRO_MAGENTA}`, color: PRO_MAGENTA }}>PRO</span>
        <div className="flex-1" />
        <Link to="/pros" className={`hidden text-[12px] uppercase leading-none tracking-[0.06em] transition-colors duration-200 hover:text-[#FF00FF] sm:inline ${FOCUS}`} style={{ color: DIM }}>
          Browse pros
        </Link>
        <button
          type="button"
          onClick={goCreate}
          className={`border-2 border-white bg-white px-[18px] py-[11px] text-[11px] font-bold uppercase leading-none tracking-[0.1em] text-black transition-colors duration-200 hover:bg-black hover:text-white ${FOCUS}`}
        >
          {ctaLabel}
        </button>
      </header>

      {/* ── Band 2 — Hero ────────────────────────────────────────────────── */}
      <section className={`${BAND_X} pt-[72px] pb-16 md:pt-[120px] md:pb-24`}>
        <div className={INNER}>
          <div className="text-[11px] font-bold uppercase leading-none tracking-[0.16em]" style={{ color: PRO_MAGENTA }}>Portal Pro</div>
          <h1 className="mt-7 max-w-[900px] text-[34px] font-bold uppercase leading-[1.06] tracking-[0.01em] md:text-[62px]">
            Get booked for<br className="hidden md:inline" /> what you do.
          </h1>
          <p className="mt-7 max-w-[620px] text-[15px] leading-[1.75] md:mt-[34px]" style={{ color: BODY }}>
            A bookable page for your craft — DJ, chef, photographer, planner, performer, whatever your thing is.
            People find you, send an offer, and you get paid through Portal.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-[22px] md:mt-11">
            <button
              type="button"
              onClick={goCreate}
              className={`border-2 border-white bg-white px-[26px] py-[18px] text-[13px] font-bold uppercase leading-none tracking-[0.08em] text-black transition-colors duration-200 hover:bg-[#FF00FF] hover:text-black ${FOCUS}`}
            >
              {ctaLabel}
            </button>
            <Link
              to={liveHref}
              className={`border-b-2 border-white pb-[3px] text-[12px] font-bold uppercase leading-none tracking-[0.08em] text-white transition-colors duration-200 hover:border-[#FF00FF] hover:text-[#FF00FF] ${FOCUS}`}
              data-testid="see-live"
            >
              See a live one →
            </Link>
            {newPageLink}
          </div>
          <div className="mt-6 text-[11px] leading-[1.6] tracking-[0.03em]" style={{ color: DIM }}>Free to set up. Nothing due to publish.</div>
        </div>
      </section>

      {/* ── Band 3 — The Pro-page mock ───────────────────────────────────── */}
      <section className={`${BAND_X} pb-16 md:pb-24`}>
        <div className={INNER}>
          {/* The wrapper stays unclipped so the offer tab can hang off the card. */}
          <div className="relative">
            <div className="relative overflow-hidden border-2 border-white bg-white text-black md:min-h-[560px]" data-testid="pro-mock">
              {/* Media mosaic — behind the text on md+, a band above it below. */}
              <div className="relative flex h-[280px] md:absolute md:inset-0 md:h-auto" aria-hidden="true">
                <div className="relative overflow-hidden bg-black" style={{ flex: '0 0 62%' }}>
                  <ProCoverVideo
                    photoUrl={`${ASSETS}/cover-poster.jpg`}
                    video={{ provider: 'file', ready: true, fileUrl: `${ASSETS}/cover.mp4` }}
                  />
                </div>
                <div className="flex flex-1 flex-col">
                  <div className="flex flex-1">
                    <div className="relative flex-1 overflow-hidden bg-[#EDEDED]"><img src={`${ASSETS}/photo-floor.jpg`} alt="" className="absolute inset-0 h-full w-full object-cover" /></div>
                    <div className="relative flex-1 overflow-hidden bg-[#EDEDED]"><img src={`${ASSETS}/photo-booth.jpg`} alt="" className="absolute inset-0 h-full w-full object-cover" /></div>
                  </div>
                  <div className="flex flex-1">
                    <div className="relative flex-1 overflow-hidden bg-[#EDEDED]"><img src={`${ASSETS}/photo-room.jpg`} alt="" className="absolute inset-0 h-full w-full object-cover" /></div>
                    <div className="flex flex-1 flex-col items-center justify-center bg-black text-white">
                      <span className="text-[30px] font-bold leading-none">+4</span>
                      <span className="mt-[5px] text-[9px] font-bold leading-none tracking-[0.1em]">PHOTOS</span>
                    </div>
                  </div>
                </div>
                <div className="pointer-events-none absolute inset-0 md:hidden" style={{ background: FADE_UP }} />
              </div>
              <div className="pointer-events-none absolute inset-0 hidden md:block" style={{ background: FADE }} aria-hidden="true" />

              {/* Text column — mirrors ProHeader.jsx; no type crosses 62%. */}
              <div className="relative flex">
                <div className="flex w-full flex-col gap-[18px] p-5 md:w-[62%] md:p-8">
                  <div className="flex items-start gap-4 md:gap-6 md:min-h-[160px]">
                    <div className="flex h-[110px] w-[110px] flex-none flex-col items-center justify-center gap-1 border-2 border-black bg-black md:h-[160px] md:w-[160px] md:gap-[6px]">
                      <span className="text-[13px] font-bold leading-none tracking-[0.1em] text-white md:text-[19px]">HOLLOW</span>
                      <span className="text-[13px] font-bold leading-none tracking-[0.1em] text-white md:text-[19px]">MOON</span>
                      <span className="text-[13px] font-bold leading-none tracking-[0.1em] md:text-[19px]" style={{ color: PRO_MAGENTA }}>SOUND</span>
                    </div>
                    <div className="flex min-w-0 flex-col justify-center gap-[10px] md:min-h-[160px]">
                      <div className="text-[11px] font-bold leading-none tracking-[0.2em]" style={{ color: PRO_MAGENTA }}>PRO</div>
                      <div className="text-[26px] font-bold uppercase leading-[1.05] tracking-[0.06em] md:text-[44px]" style={{ overflowWrap: 'anywhere' }}>{MOCK.name}</div>
                      <div className="flex flex-wrap gap-2">
                        {MOCK.crafts.map((c) => (
                          <span key={c} className="border-2 bg-white px-3 py-2 text-[12px] font-bold uppercase leading-none tracking-[0.1em]" style={{ borderColor: PRO_MAGENTA, color: PRO_MAGENTA }}>{craftLabel(c)}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-[13px] leading-none tracking-[0.05em]">
                    <span className="font-bold" style={{ color: GREEN_ON_WHITE }}>12 VERIFIED GIGS</span>
                    <span style={{ color: MUTED }}>ESCROW-VERIFIED BOOKING</span>
                  </div>
                  <p className="pl-4 font-serif text-[22px] italic leading-[1.35] text-black" style={{ borderLeft: `4px solid ${PRO_MAGENTA}` }}>{MOCK.vibe}</p>
                  <div className="flex flex-wrap items-center gap-[18px] text-[12px] leading-none tracking-[0.05em]" style={{ color: MUTED }}>
                    <span className="uppercase">{MOCK.city}</span>
                    <span className="text-black underline">{MOCK.site}</span>
                  </div>
                  <div className="text-[12px] font-bold uppercase leading-none tracking-[0.05em]" style={{ color: MUTED }}>Managed by {MOCK.owner} →</div>
                  <div className="flex items-center gap-3">
                    <div className="flex h-14 w-14 flex-none items-center justify-center border-2 border-black bg-[#F3F4F6] text-[22px] font-bold leading-none text-black">R</div>
                    <div>
                      <div className="text-[14px] font-bold uppercase leading-[1.2] text-black">
                        {MOCK.owner}<span className="ml-2 font-normal normal-case" style={{ color: MUTED }}>@{MOCK.handle}</span>
                      </div>
                      <div className="mt-[3px] text-[13px] leading-none" style={{ color: MUTED }}>owner</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* "Send an offer" — overhangs the card's corner on md+ so the magenta reads against black;
                a full-width bar beneath it on small screens so it can't clip off-screen. */}
            <div
              className="block w-full px-4 py-[10px] text-center text-[11px] font-bold uppercase leading-none tracking-[0.12em] text-black md:absolute md:-bottom-[18px] md:-right-[2px] md:w-auto md:text-left"
              style={{ background: PRO_MAGENTA }}
              aria-hidden="true"
            >
              Send an offer
            </div>
          </div>
          <div className="mt-[30px] text-[11px] leading-[1.5] tracking-[0.04em]" style={{ color: DIM }}>
            {MOCK.url} — anyone can book from this page, account or not.
          </div>
        </div>
      </section>

      {/* ── Band 4 — Three steps ─────────────────────────────────────────── */}
      <section style={{ borderTop: `2px solid ${RULE}` }}>
        {STEPS.map((s) => (
          <div key={s.n} style={{ borderBottom: `2px solid ${RULE}` }}>
            <div className={`${INNER} ${BAND_X} flex flex-col gap-3 py-8 md:flex-row md:gap-14 md:py-11`}>
              <span className="flex-none text-[13px] font-bold leading-[1.4] md:w-16" style={{ color: PRO_MAGENTA }}>{s.n}</span>
              <span className="flex-none text-[20px] font-bold uppercase leading-[1.3] tracking-[0.04em] md:w-[300px]">{s.t}</span>
              <span className="text-[14px] leading-[1.75]" style={{ color: BODY }}>{s.d}</span>
            </div>
          </div>
        ))}
      </section>

      {/* ── Band 5 — What sits on your page ──────────────────────────────── */}
      <section className={`${BAND_X} py-14 md:py-20`}>
        <div className={`${INNER} grid items-start gap-10 md:grid-cols-[minmax(0,1fr)_420px] md:gap-[72px]`}>
          <div>
            <div className="text-[11px] font-bold uppercase leading-none tracking-[0.16em]" style={{ color: DIM }}>What sits on your page</div>
            <ul className="mt-[30px] flex flex-col gap-[18px]">
              {CLAIMS.map((c) => (
                <li key={c} className="flex gap-[18px]">
                  <span className="w-4 flex-none text-[13px] font-bold leading-[1.7]" style={{ color: PRO_MAGENTA }} aria-hidden="true">→</span>
                  <span className="text-[15px] leading-[1.7]">{c}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-4 text-black">
            {/* Panel 1 — Availability */}
            <div className="border-2 border-white bg-white">
              <div className="flex justify-between gap-2 border-b-2 border-black px-4 py-3 text-[10px] font-bold uppercase leading-none tracking-[0.12em]">
                <span>Availability</span>
                <span className="font-normal" style={{ color: MUTED }}>Times in PDT</span>
              </div>
              <div className="grid grid-cols-7 gap-1 p-4">
                {DAYS.map((d) => <span key={d} className="text-center text-[10px] font-bold leading-none" style={{ color: MUTED }}>{d}</span>)}
                {DAYS.map((d, i) => <Slot key={`am-${d}`} label="AM" on={AM_ON.includes(i)} />)}
                {DAYS.map((d, i) => <Slot key={`pm-${d}`} label="PM" on={PM_ON.includes(i)} />)}
              </div>
            </div>

            {/* Panel 2 — Share this pro. A real, scannable code for the real page "See a live one" opens. */}
            <div className="border-2 border-white bg-white">
              <div className="border-b-2 border-black px-4 py-3 text-[10px] font-bold uppercase leading-none tracking-[0.12em]">Share this pro</div>
              <div className="flex gap-4 p-4">
                <div className="flex h-[76px] w-[76px] flex-none items-center justify-center border-2 border-black bg-[#F5F5F5]">
                  <BrandedQR value={`https://${shareUrl}`} size={72} />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-[10px]">
                  <span className="break-all text-[11px] leading-[1.5]" style={{ color: MUTED }}>{shareUrl}</span>
                  <span className="self-start border-2 border-black px-[10px] py-[7px] text-[9px] font-bold uppercase leading-none tracking-[0.1em]">Copy link</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Band 6 — Founding pricing ────────────────────────────────────── */}
      <section className={`${BAND_X} py-14 md:py-[72px]`} style={{ borderTop: `2px solid ${RULE}` }}>
        <div className={`${INNER} flex flex-col gap-10 md:flex-row md:items-end md:justify-between md:gap-16`}>
          <div>
            <div className="text-[11px] font-bold uppercase leading-none tracking-[0.16em]" style={{ color: PRO_MAGENTA }}>Founding pros</div>
            <div className="mt-5 text-[30px] font-bold uppercase leading-[1.08] tracking-[0.02em] md:text-[42px]">
              Free for your<br className="hidden md:inline" /> first 6 months.
            </div>
            <div className="mt-5 max-w-[480px] text-[13px] leading-[1.7]" style={{ color: DIM }}>
              Setting up and publishing is free. A standard page only starts billing after you go live.
            </div>
          </div>
          <div className="flex w-full flex-none flex-col gap-[14px] text-[12px] leading-[1.6] md:w-[300px]" style={{ color: DIM }}>
            <div className="flex justify-between gap-4 pb-3" style={{ borderBottom: `2px solid ${RULE}` }}>
              <span className="uppercase">Standard</span><span className="text-white">$10 / mo CAD</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="uppercase">Party for Good</span><span className="text-white">Always free</span>
            </div>
            <div className="text-[11px]" style={{ color: '#B5B5B5' }}>Verified charities &amp; non-profits.</div>
          </div>
        </div>
      </section>

      {/* ── Band 7 — Closing CTA ─────────────────────────────────────────── */}
      <section className={`${BAND_X} py-14 md:py-[72px]`} style={{ borderTop: `2px solid ${RULE}` }}>
        <div className={`${INNER} flex flex-col gap-8 md:flex-row md:items-center md:justify-between md:gap-10`}>
          <div className="text-[24px] font-bold uppercase leading-[1.25] tracking-[0.03em] md:text-[28px]">
            Get booked for<br className="hidden md:inline" /> what you do.
          </div>
          <div className="flex flex-wrap items-center gap-[22px]">
            <button
              type="button"
              onClick={goCreate}
              className={`self-start border-2 border-white bg-white px-7 py-5 text-[13px] font-bold uppercase leading-none tracking-[0.08em] text-black transition-colors duration-200 hover:bg-[#FF00FF] hover:text-black ${FOCUS}`}
            >
              {ctaLabel} →
            </button>
            {hasPage && (
              <Link to="/pro/setup?new=1" className={`border-b-2 border-white pb-[3px] text-[12px] font-bold uppercase leading-none tracking-[0.08em] text-white transition-colors duration-200 hover:border-[#FF00FF] hover:text-[#FF00FF] ${FOCUS}`}>
                + New Pro page
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Slot({ label, on }) {
  return on ? (
    <span className="border-2 border-black py-2 text-center text-[10px] font-bold leading-none text-black" style={{ background: PRO_MAGENTA }}>{label}</span>
  ) : (
    <span className="border-2 border-[#E5E7EB] py-2 text-center text-[10px] leading-none" style={{ color: MUTED }}>{label}</span>
  );
}
